import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone, ArrowRight } from "lucide-react";
import { SITE } from "@/lib/site";

const sections = [
  {
    id: "scope",
    title: "1. Scope of this Policy",
    body: [
      "This Privacy Policy explains how BackHome Restoration Inc. (\"BackHome\", \"we\", \"our\" or \"us\") collects, uses, discloses and protects personal information of visitors, clients, restoration partners, insurance professionals and members of the public who interact with our website, contact forms, telephone line or services.",
      "By using our website or contacting us, you acknowledge that you have read this Policy and consent to the practices described below.",
    ],
  },
  {
    id: "info",
    title: "2. Information We Collect",
    body: [
      "We only collect personal information that is necessary to respond to inquiries, coordinate restoration and contents services, support insurance claims, and run our business responsibly.",
    ],
    list: [
      "Identification: name, company, role.",
      "Contact details: email address, telephone number, mailing or service address.",
      "Service details: description of the loss, property type, claim or policy number, photos and documentation you choose to share.",
      "Technical data: IP address, browser type, device, pages visited (via standard analytics).",
    ],
  },
  {
    id: "use",
    title: "3. How We Use Your Information",
    list: [
      "To respond to your inquiry and dispatch the appropriate crew or coordinator.",
      "To coordinate with restoration contractors, insurance adjusters and property managers acting on your behalf.",
      "To document inventories, pack-outs, storage and pack-back activities for claim purposes.",
      "To maintain internal records, invoicing, payments and quality assurance.",
      "To comply with legal, regulatory and insurance obligations.",
    ],
  },
  {
    id: "share",
    title: "4. When We Share Information",
    body: [
      "We do not sell personal information. We only share it where it is needed to deliver our services or comply with the law:",
    ],
    list: [
      "With your restoration contractor, insurance adjuster or property manager when you have engaged them.",
      "With trusted service providers (e.g. storage, transportation, IT and email infrastructure) bound by confidentiality.",
      "When required by law, court order, regulator or to protect our rights, staff or clients.",
    ],
  },
  {
    id: "security",
    title: "5. Storage & Security",
    body: [
      "Personal information submitted through our website is stored on secured infrastructure with access restricted to authorized personnel. Physical files and documentation are kept in controlled environments. We retain personal information only for as long as required to deliver our services and meet legal, accounting and insurance requirements.",
    ],
  },
  {
    id: "rights",
    title: "6. Your Rights",
    body: [
      "Subject to applicable law (including Canada's PIPEDA), you may request to access, correct or delete the personal information we hold about you, or withdraw your consent to its use. Some information may need to be retained for legal or contractual reasons. To exercise any right, contact us using the details below.",
    ],
  },
  {
    id: "cookies",
    title: "7. Cookies & Analytics",
    body: [
      "Our website may use cookies and similar technologies for essential functionality, security and basic anonymized analytics. You can control cookies through your browser settings; disabling them may affect site features.",
    ],
  },
  {
    id: "changes",
    title: "8. Changes to this Policy",
    body: [
      "We may update this Policy from time to time. The latest version will always be available on this page with an updated effective date.",
    ],
  },
  {
    id: "contact",
    title: "9. Contact Us",
    body: [
      "Questions about privacy, your information or this Policy? Reach our coordination team and we will respond promptly.",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="page-privacy" className="bg-[hsl(var(--gaza-paper))]">
      {/* HERO */}
      <section className="relative bg-[hsl(var(--gaza-teal-deep))] text-white overflow-hidden" data-testid="privacy-hero">
        <div className="grain absolute inset-0" aria-hidden />
        <div className="absolute top-0 left-0 right-0 h-1 ember-stripe siren-sweep" />
        <div className="container-gaza relative py-24 md:py-32">
          <div className="live-pill mb-8 reveal-up" data-reveal>
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Your information, protected</span>
          </div>
          <h1 className="display-chunky text-5xl md:text-7xl lg:text-[6rem] leading-[0.95] max-w-4xl reveal-up" data-reveal data-reveal-delay="120">
            Privacy <span className="text-[hsl(var(--gaza-crimson-bright))]">Policy</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed mt-8 reveal-up" data-reveal data-reveal-delay="240">
            We deal in trust as much as trucks and tools. Here is exactly how we handle the personal information you share with BackHome Restoration Inc.
          </p>
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/55 font-extrabold mt-10 reveal-up" data-reveal data-reveal-delay="320">
            Effective date: February 2026
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="container-gaza py-20 md:py-28 grid md:grid-cols-12 gap-12" data-testid="privacy-body">
        {/* Table of contents */}
        <aside className="md:col-span-4 lg:col-span-3">
          <div className="md:sticky md:top-24">
            <div className="eyebrow mb-4">On this page</div>
            <ul className="space-y-2 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-[hsl(var(--gaza-teal-mist))] hover:text-[hsl(var(--gaza-crimson))] font-semibold transition" data-testid={`privacy-toc-${s.id}`}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-10 p-6 border border-[hsl(var(--gaza-line))] bg-white">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--gaza-crimson))] font-extrabold mb-3">Need help?</div>
              <p className="text-sm text-[hsl(var(--gaza-teal-mist))] leading-relaxed mb-4">For privacy requests or questions, reach the coordination team directly.</p>
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 text-[hsl(var(--gaza-teal))] font-extrabold text-sm" data-testid="privacy-phone">
                <Phone className="w-4 h-4 text-[hsl(var(--gaza-crimson))]" /> {SITE.phone}
              </a>
            </div>
          </div>
        </aside>

        {/* Sections */}
        <div className="md:col-span-8 lg:col-span-9 space-y-14">
          {sections.map((s) => (
            <article key={s.id} id={s.id} data-reveal className="reveal-up scroll-mt-28" data-testid={`privacy-section-${s.id}`}>
              <h2 className="display-chunky text-3xl md:text-4xl text-[hsl(var(--gaza-teal))] leading-tight mb-5">
                {s.title}
              </h2>
              {s.body?.map((p) => (
                <p key={p} className="text-[hsl(var(--gaza-teal-mist))] leading-relaxed text-base md:text-lg mb-4">{p}</p>
              ))}
              {s.list && (
                <ul className="space-y-3 mt-4">
                  {s.list.map((item) => (
                    <li key={item} className="flex gap-3 text-[hsl(var(--gaza-teal-mist))] leading-relaxed text-base md:text-lg">
                      <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gaza-crimson))] mt-2.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}

          {/* Contact card */}
          <div className="border border-[hsl(var(--gaza-line))] bg-white p-8 md:p-10" data-testid="privacy-contact-card">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="eyebrow mb-3">Privacy Officer</div>
                <h3 className="display-chunky text-3xl text-[hsl(var(--gaza-teal))] leading-tight mb-2">BackHome Restoration Inc.</h3>
                <p className="text-[hsl(var(--gaza-teal-mist))] leading-relaxed max-w-md">Serving Toronto & the Greater Toronto Area. Coordinators available 24/7 for privacy and service requests.</p>
              </div>
              <div className="flex flex-col gap-3">
                <a href={SITE.phoneHref} className="btn-primary" data-testid="privacy-contact-call"><Phone className="w-4 h-4" /> {SITE.phone}</a>
                <Link to="/contact" className="btn-outline-dark" data-testid="privacy-contact-link"><Mail className="w-4 h-4" /> Send a Message <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
